import React, { useState, useEffect } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import * as mime from 'react-native-mime-types';
import {
    MessageCircle,
    Link,
    Phone,
    Copy,
    Trash2,
    Megaphone,
    Bell
} from "lucide-react";
import PhoneInput, { isValidPhoneNumber, isPossiblePhoneNumber } from 'react-phone-number-input';
import EmojiPicker from 'emoji-picker-react';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FlagIcon from '@mui/icons-material/Flag';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Collapse from '@mui/material/Collapse';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import ImageIcon from '@mui/icons-material/Image';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import Crop169Icon from '@mui/icons-material/Crop169';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import Avatar from '@mui/material/Avatar';
import MessageIcon from '@mui/icons-material/Message';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useFileUploadHook } from '@Hooks/useFileUploadHook';
import { useReduxFileStorage } from '@Hooks/reduxFileStorageHook/useReduxFileStorage';
import KaiziqLeadsConnectionService from '@Helpers/URLHelpers/WeboutService/KaiziqLeadsConnectionService';
import FileService from '@Helpers/URLHelpers/FileService';
import ErrorHandler from '@Root/ErrorHandler';
import './WhatsAppTemplateBuilder.scss';

const initialState = {
    header: { format: '', str: '' },
    body: 'We have an exciting offer {{1}} \n Are you interested in hearing more ?',
    footer: '',
    buttons: []
};

function WhatsappTemplateBuilder(props) {

    const [cursor, setCursor] = useState('auto');
    const [header, setHeader] = useState(initialState.header);
    const [body, setBody] = useState(initialState.body);
    const [footer, setFooter] = useState(initialState.footer);
    const [buttons, setButtons] = useState(initialState.buttons);
    const [category, setCategory] = useState("MARKETING");
    const [templateName, setTemplateName] = useState('');
    const [variableExamples, setVariableExamples] = useState({});
    const [headerVariableExamples, setHeaderVariableExamples] = useState({});
    const [errors, setErrors] = useState({});
    const [approvalStatus, setApprovalStatus] = useState(null);
    const [contactNumber, setContactNumber] = useState('');
    const [isPossibleNumber, setIsPossibleNumber] = useState(false);
    const [isValidNumber, setIsValidNumber] = useState(false);
    const bodyRef = React.useRef(null);
    const headerRef = React.useRef(null);
    const { currentLeadsId, currentPhoneNumber, templateProtection } = useReduxLeads();
    const [showEmoji, setShowEmoji] = useState(false);
    const [headerIncluded, setHeaderIncluded] = useState(false);
    const [headerFormat, setHeaderFormat] = useState('');
    const [footerIncluded, setFooterIncluded] = useState(templateProtection);
    const [buttonsIncluded, setButtonsIncluded] = useState(templateProtection);
    const { validate, fileType, dimensions } = useFileUploadHook();
    const { currentProjectId } = useReduxProject();
    const { fileStorage, fetchFileStorage, filesCount, currentFiles } = useReduxFileStorage();
    const fileService = new FileService();
    const kaiziqLeadsConnectionService = new KaiziqLeadsConnectionService();
    const errorHandler = new ErrorHandler();

    useEffect(()=> {
        renderData();
    }, []);

    useEffect(()=> {
        if(category === 'UTILITY') {
            setFooter(footer === 'Reply "STOP" to opt out' ? '' : footer);
            setButtons((prev)=> prev.filter((item)=> item.text === 'STOP' ? null : item));
        } else {
            setFooter(templateProtection ? 'Reply "STOP" to opt out' : '');
            let findButton = buttons.find((item)=> item.text === 'STOP');
            if(!findButton) {
                setButtons((prev)=> templateProtection ? [...prev, {text: 'STOP', type: 'QUICK_REPLY', value: ''}] : prev);
            }
        }
    }, [category])

    const renderData = async()=> {
        setCursor('wait');
        await fetchFileStorage('LEADS_WHATSAPP', currentLeadsId);
        setCursor('auto');
    }

    const handleHeaderToggle = (event) => {
        if (!event.target.checked) {
            setHeader({ format: '', str: '' });
            setHeaderFormat('');
        }
        setHeaderIncluded(event.target.checked);
    };

    const handleHeaderFormatChange = (event) => {
        setHeaderFormat(event.target.value);
        setHeader({ format: event.target.value, str: '' });
    };

    const handleFooterToggle = (event) => {
        if (!event.target.checked) {
            setFooter('');
        }
        setFooterIncluded(event.target.checked);
    };

    const handleButtonsToggle = (event) => {
        const checked = event.target.checked;
        setButtonsIncluded(checked);
        if (checked) {
            setButtons([
                { type: 'QUICK_REPLY', text: '', value: '' }
            ]);
        } else {
            setButtons([]);
        }
    };

    const handleAddButton = () => {
        if (buttons.length >= 3) return;
        setButtons([
            ...buttons,
            { type: 'QUICK_REPLY', text: '', value: '' }
        ]);
    };

    const handleDeleteButton = (index) => {
        const newButtons = buttons.filter((_, i) => i !== index);
        setButtons(newButtons);
    };

    const handleButtonChange = (index, field, value) => {
        const newButtons = [...buttons];
        newButtons[index][field] = value;
        setButtons(newButtons);
    };

    const handleCategoryChange = (value) => {
        setCategory(value);
    };

    const handleHeaderChange = (value) => {
        let corrected = autoCorrectVariables(value);
        const matches = corrected.match(/{{\d+}}/g) || [];
        if (matches.length > 1) {
            corrected = corrected.replace(matches[1], '');
        }
        setHeader(prev => ({
            ...prev,
            format: "text",
            str: corrected
        }));
    };

    const handleHeaderFileChange = async (e)=> {
        e.preventDefault();
        const file = e.target.files[0];
        if (!!file) {
            const result = await validate(file);
            if(result.isValid) {
                let fileType = result.fileType;
                if(fileType === 'IMAGE') {
                    setHeader({ format: 'image', str: URL.createObjectURL(file), file });
                } else if(fileType === 'VIDEO') {
                    setHeader({ format: 'video', str: URL.createObjectURL(file), file });
                } else {
                    setHeader({ format: 'DOCUMENT', str: URL.createObjectURL(file), file });
                }
            } else {
                SnackbarManager.show(result.error, 'error');
            }
        }
    }

    const renderHeaderInput = () => {
        switch (headerFormat) {
            case 'text':
                return (
                    <>
                        <FormControl className="whatsappBuilder-footerInputBox"
                            fullWidth
                            sx={{
                                mt: 2
                            }}>
                            <Input
                                id="header-text"
                                disableUnderline
                                inputRef={headerRef}
                                multiline
                                fullWidth
                                rows={2}
                                value={header.str}
                                onChange={(e) => handleHeaderChange(e.target.value)}
                                inputProps={{ maxLength: 60 }}
                                sx={{background: 'transparent', p: 1}}
                            />
                            <Input
                                fullWidth
                                disabled
                                disableUnderline
                                sx={{pr: 1, position: 'absolute', bottom: 0, right: 0, width: 'max-content'}}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <div className="whatsappBuilder-footerCount">
                                            {header.str.length}/60
                                        </div>
                                    </InputAdornment>
                                }
                            />
                        </FormControl>
                        {detectedHeaderVariables < 1 &&
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginTop: '10px'
                                }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handleHeaderVariable}
                                    className="whatsappTemplateBuilder-addVarBtn"
                                >
                                    + Add Variable
                                </Button>
                            </div>
                        }
                    </>
                );
            case 'image':
                return (
                    <div sx={{ marginTop: '12px' }}>
                        <input
                            accept='image/*'
                            type='file'
                            id='select-image-header'
                            style={{ display: 'none' }}
                            onChange={handleHeaderFileChange}
                        />
                        <label htmlFor='select-image-header'>
                            <Button variant='outlined' component='span' sx={{mt: 1}}>UPLOAD IMAGE</Button>
                        </label>
                    </div>
                );
            case 'video':
                return (
                    <div sx={{ marginTop: '12px' }}>
                        <input
                            accept='video/*'
                            type='file'
                            id='select-video-header'
                            style={{ display: 'none' }}
                            onChange={handleHeaderFileChange}
                        />
                        <label htmlFor='select-video-header'>
                            <Button variant='outlined' component='span' sx={{mt: 1}}>UPLOAD VIDEO</Button>
                        </label>
                    </div>
                );
            case 'document':
                return (
                    <div sx={{ marginTop: '12px' }}>
                        <input
                            accept='.doc,.docx,.pdf'
                            type='file'
                            id='select-document-header'
                            style={{ display: 'none' }}
                            onChange={handleHeaderFileChange}
                        />
                        <label htmlFor='select-document-header'>
                            <Button variant='outlined' component='span' sx={{mt: 1}}>UPLOAD DOCUMENT</Button>
                        </label>
                    </div>
                );
            default:
                return null;
        }
    };

    const handleSaveTemplate = () => {
        const isValid = deepMetaValidator();
        if (!isValid) return;
        const strictError = validateTemplate();
        if (strictError) {
            setErrors({ global: strictError });
            return;
        }
        setErrors({});
        const components = [];
        if (headerIncluded && header.format) {
            if (header.format === 'text') {
                const headerComponent = {
                    type: "HEADER",
                    format: "TEXT",
                    text: header.str
                };

                if (detectedHeaderVariables.length > 0) {
                    headerComponent.example = {
                        header_text: detectedHeaderVariables.map(
                            v => headerVariableExamples[v] || "sample"
                        )
                    };
                }
                components.push(headerComponent);
            } else {
                components.push({
                    type: "HEADER",
                    format: header.format.toUpperCase(),
                    example: {
                        header_handle: '{mediaId}'
                    }
                });
            }
        }
        const bodyComponent = {
            type: "BODY",
            text: body
        };
        if (detectedVariables.length > 0) {
            bodyComponent.example = {
                body_text: [
                    detectedVariables.map(v => variableExamples[v] || "sample")
                ]
            };
        }
        components.push(bodyComponent);
        if (footerIncluded && footer) {
            components.push({
                type: "FOOTER",
                text: footer
            });
        }
        if (buttonsIncluded) {
            const filteredButtons = buttons.filter(b => b.text.trim() !== "");
            if (filteredButtons.length) {
                components.push({
                    type: "BUTTONS",
                    buttons: filteredButtons.map(btn => {
                        if (btn.type === 'QUICK_REPLY') {
                            return {
                                type: "QUICK_REPLY",
                                text: btn.text
                            };
                        }
                        if (btn.type === 'URL') {
                            return {
                                type: "URL",
                                text: btn.text,
                                url: btn.value
                            };
                        }
                        if (btn.type === 'PHONE_NUMBER') {
                            return {
                                type: "PHONE_NUMBER",
                                text: btn.text,
                                phone_number: btn.value
                            };
                        }
                        if (btn.type === 'COPY_CODE') {
                            return {
                                type: "COPY_CODE",
                                text: btn.text,
                                example: btn.value
                            };
                        }
                        return null;
                    })
                });
            }
        }
        const payload = {
            name: templateName,
            language: "en",
            category: category,
            components
        };
        createTemplate(payload);
    };

    const createTemplate = async (body) => {
        setCursor('wait');
        try {
            let bodyObj = {
                template: body,
                leadsId: currentLeadsId,
                templateName: templateName,
            }
            if(!!header.file) {
                let file = header.file;
                const url = await convertFileToBase64(file);
                let newFileObj = {
                    file: url,
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type,
                    projectId: currentProjectId
                }
                bodyObj.file = {
                    ...newFileObj,
                    phoneNumberId: currentPhoneNumber?.id
                }
            }
            let response = await kaiziqLeadsConnectionService.createWhatsappTemplate(bodyObj);
            if (response.status < 400) {
                SnackbarManager.show(response.message, 'success');
                props.onClose();
                props.renderData();
            } else {
                SnackbarManager.show(response.message, 'error');
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const handleHeaderVariable = () => {
        const textarea = headerRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const headerText = header?.str || "";
        const existingVars = headerText.match(/{{\d+}}/g) || [];
        if (existingVars.length >= 1) return;
        const newText =
            headerText.substring(0, start) +
            "{{1}}" +
            headerText.substring(end);
        setHeader(prev => ({
            ...prev,
            format: "text",
            str: newText
        }));
        setTimeout(() => {
            textarea.focus();
            textarea.selectionStart = textarea.selectionEnd = start + 5;
        }, 0);
        return;
    }

    const handleAddVariable = () => {
        const textarea = bodyRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const matches = body.match(/{{\d+}}/g) || [];
        const numbers = matches.map(v => parseInt(v.replace(/[{}]/g, "")));
        const nextNumber = numbers.length ? Math.max(...numbers) + 1 : 1;
        const newText =
            body.substring(0, start) +
            `{{${nextNumber}}}` +
            body.substring(end);

        setBody(newText);
    };

    const handleBodyChange = (value) => {
        const corrected = autoCorrectVariables(value);
        const matches = corrected.match(/{{\d+}}/g) || [];
        const unique = [...new Set(matches)];
        setVariableExamples(prev => {
            const updated = {};
            unique.forEach(v => {
                if (prev[v]) updated[v] = prev[v];
            });
            return updated;
        });
        setBody(corrected);
    };

    const handleEmojiClick = (emojiData) => {
        const textarea = bodyRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newValue =
          textarea.value.substring(0, start) +
          emojiData.emoji +
          textarea.value.substring(end);
        setBody(newValue);
        setShowEmoji(false);
    };

    const validateTemplate = () => {
        const variableMatches = body.match(/{{\d+}}/g) || [];
        const uniqueVariables = [...new Set(variableMatches)];
        const numbers = uniqueVariables.map(v =>
            parseInt(v.replace(/[{}]/g, ''))
        ).sort((a, b) => a - b);
        if (numbers.length > 60)
            return "Maximum 60 variables allowed in body";
        if (numbers.length > 0) {
            if (numbers[0] !== 1)
                return "Variables must start from {{1}}";
            for (let i = 0; i < numbers.length; i++) {
                if (numbers[i] !== i + 1)
                    return "Variables must be sequential (no skipping numbers)";
            }
        }
        if (!buttonsIncluded) return null;
        const activeButtons = buttons.filter(b => b.text.trim() !== "");
        if (activeButtons.length > 10)
            return "Maximum 10 buttons allowed";
        const quickReplies = activeButtons.filter(b => b.type === 'QUICK_REPLY');
        const urlButtons = activeButtons.filter(b => b.type === 'URL');
        const phoneButtons = activeButtons.filter(b => b.type === 'PHONE_NUMBER');
        const copyButtons = activeButtons.filter(b => b.type === 'COPY_CODE');
        if (quickReplies.length > 3)
            return "Maximum 3 Quick Reply buttons allowed";
        if (urlButtons.length > 2)
            return "Maximum 2 URL buttons allowed";
        if (phoneButtons.length > 1)
            return "Only 1 Phone Number button allowed";
        if (copyButtons.length > 1)
            return "Only 1 Copy Code button allowed";
        if (props.category === "AUTHENTICATION") {
            if (activeButtons.length !== 1)
                return "Authentication templates must have exactly 1 OTP button";
            if (activeButtons[0].type !== "QUICK_REPLY")
                return "Authentication template only supports OTP Quick Reply button";
        }
        for (let btn of urlButtons) {
            const matches = btn.value.match(/{{\d+}}/g) || [];
            if (matches.length > 2)
                return "URL button supports maximum 2 variables";
        }
        for (let btn of activeButtons) {
            if (!btn.text.trim())
                return "Button text cannot be empty";
            if (btn.text.length > 25)
                return "Button text cannot exceed 25 characters";
            if (btn.type === 'URL') {
                if (!btn.value || !btn.value.trim()) {
                    return "URL button must have URL";
                }
                if (!isValidUrl(btn.value.trim())) {
                    return "Please enter a valid URL (https://...)";
                }
            }
            if (btn.type === 'PHONE_NUMBER') {
                if (!btn.value || !btn.value.trim())
                    return "Phone button must have phone number";
                if (!isValidPhoneNumber(btn.value.trim()))
                    return "Phone number must be in international format (e.g. +1234567890)";
            }
        }
        return null;
    };

    const isValidUrl = (url) => {
        const regex = /^https:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/;
        return regex.test(url);
    };

    const isValidPhoneNumber = (phone) => {
        const regex = /^\+[1-9]\d{7,14}$/;
        return regex.test(phone);
    };

    const detectedVariables = React.useMemo(() => {
        const matches = body.match(/{{\d+}}/g);
        if (!matches) return [];
        const unique = [...new Set(matches)];
        return unique.sort((a, b) =>
            parseInt(a.replace(/[{}]/g, "")) -
            parseInt(b.replace(/[{}]/g, ""))
        );
    }, [body]);

    const autoCorrectVariables = (text) => {
        const matches = text.match(/{{\d+}}/g);
        if (!matches) return text;
        const unique = [...new Set(matches)];
        let corrected = text;
        unique.forEach((v, index) => {
            const newVar = `{{${index + 1}}}`;
            const regex = new RegExp(v.replace(/[{}]/g, "\\$&"), "g");
            corrected = corrected.replace(regex, newVar);
        });
        return corrected;
    };

    const detectedHeaderVariables = React.useMemo(() => {
        if (header.format !== "text") return [];
        const matches = header.str.match(/{{\d+}}/g);
        if (!matches) return [];
        const unique = [...new Set(matches)];
        return unique.sort((a, b) =>
            parseInt(a.replace(/[{}]/g, "")) -
            parseInt(b.replace(/[{}]/g, ""))
        );
    }, [header]);

    const deepMetaValidator = () => {
        const newErrors = {};
        if (!category.trim()) {
            setErrors({ category: "Template category is required" });
            return;
        }
        if (!templateName.trim()) {
            setErrors({ templateName: "Template name is required" });
            return;
        }
        if (!/^[a-z0-9_]+$/.test(templateName)) {
            setErrors({ templateName: "Only lowercase letters, numbers and underscore allowed" });
            return;
        }
        if (!body.trim()) {
            newErrors.body = "Body is required";
        }
        const startsWithVariable = /^\s*\{\{\d+\}\}/.test(body);
        const endsWithVariable = /\{\{\d+\}\}\s*$/.test(body);

        if (startsWithVariable || endsWithVariable) {
            newErrors.body =
                "Body cannot start or end with a variable (Meta restriction)";
        }
        if (body.length > 1024) {
            newErrors.body = "Body exceeds 1024 characters (Meta limit)";
        }
        const invalidVar = body.match(/{{[^0-9]+}}/g);
        if (invalidVar) {
            newErrors.body = "Invalid variable format detected";
        }
        if (headerIncluded && header.format === 'text' && header.str.length > 60) {
            newErrors.header = "Header text exceeds 60 characters";
        }
        if (footerIncluded && footer.length > 60) {
            newErrors.footer = "Footer exceeds 60 characters";
        }
        if (buttonsIncluded) {
            const activeButtons = buttons.filter(b => b.text.trim());
            activeButtons.forEach((btn, index) => {
                let buttonName = 'button_' + (index + 1);
                if (btn.text.length > 25) {
                    newErrors[buttonName] =
                        "Button text exceeds 25 characters";
                }
                if (btn.type === 'URL' && !btn.value) {
                    newErrors[buttonName] =
                        "URL button requires URL";
                }
                if (btn.type === 'PHONE_NUMBER' && !btn.value) {
                    newErrors[buttonName] =
                        "Phone button requires number";
                }
            });
        }
        if (detectedVariables.length > 0) {
            detectedVariables.forEach(v => {
                if (!variableExamples[v] || !variableExamples[v].trim()) {
                    newErrors[`variable_${v}`] =
                        `Default value required for ${v}`;
                }
            });
        }
        if (headerIncluded && detectedHeaderVariables.length > 0) {
            detectedHeaderVariables.forEach(v => {
                if (!headerVariableExamples[v] || !headerVariableExamples[v].trim()) {
                    newErrors[`header_variable_${v}`] =
                        `Header default required for ${v}`;
                }
            });
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleTemplateNameChange = (e) => {
        let value = e.target.value;
        value = value.replace(/\s+/g, "_");
        value = value.toLowerCase();
        value = value.replace(/[^a-z0-9_]/g, "");
        setTemplateName(value);
    };

    const applyFormat = (symbol) => {
        const textarea = bodyRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        if (start === end) return;
        const selectedText = body.substring(start, end);
        const formattedText = `${symbol}${selectedText}${symbol}`;
        const newText =
            body.substring(0, start) +
            formattedText +
            body.substring(end);
        setBody(newText);

        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(
                start + symbol.length,
                end + symbol.length
            );
        }, 0);
    };

    const renderWhatsAppFormatting = (text) => {
        if (!text) return "";
        let formatted = text;
        formatted = formatted.replace(/\*(.*?)\*/g, "<strong>$1</strong>");
        formatted = formatted.replace(/_(.*?)_/g, "<em>$1</em>");
        formatted = formatted.replace(/~(.*?)~/g, "<del>$1</del>");
        formatted = formatted.replace(/`(.*?)`/g, "<code>$1</code>");
        if (detectedVariables.length > 0) {
            detectedVariables.forEach(v => {
                let newValue = variableExamples[v]?.trim();
                formatted = formatted.replace(v, !newValue ? v : newValue);
            });
        }
        return formatted;
    };

    const allErrorMessages = Object.values(errors);

    const allErrorMessagesWithField = Object.entries(errors).map(
        ([key, message]) => `${key.replace(/_/g, ' ')}: ${message}`
    );

    const convertFileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });

    const handleContactNumberChange = (index, newNumber) => {
        setContactNumber(newNumber);
        setIsPossibleNumber(newNumber && isPossiblePhoneNumber(newNumber));
        setIsValidNumber(newNumber && isValidPhoneNumber(newNumber));
        if(isValidNumber) {
            const newButtons = [...buttons];
            newButtons[index]['value'] = newNumber;
            setButtons(newButtons);
        }
    };

    return (
        <div className="whatsappBuilder-root" style={{cursor: cursor}}>
            <div className="whatsappBuilder-grid">
                <div className="whatsappBuilder-left">
                    <div className="whatsappBuilder-editorPaper">
                        <div className="whatsappBuilder-topbar">
                            <div className="whatsappBuilder-brand">
                                <div className="whatsappBuilder-brandIcon">
                                    {
                                        category === 'UTILITY' ?
                                            <Bell className="whatsappBuilder-brandIconSvg"  />
                                        :
                                            <Megaphone className="whatsappBuilder-brandIconSvg"  />
                                    }
                                </div>
                                <div className="whatsappBuilder-brandTitle">
                                    Template Builder
                                </div>
                            </div>
                            <IconButton className="whatsappBuilder-closeBtn" onClick={props.onClose}>
                                <CloseIcon className="whatsappBuilder-closeBtnIcon" />
                            </IconButton>
                        </div>
                        <div className="whatsappBuilder-card whatsappBuilder-categoryCard">
                            <CardHeader
                                className="whatsappBuilder-cardHeader whatsappBuilder-categoryHeader"
                                avatar={
                                    <div className="whatsappBuilder-cardIconWrap">
                                        <Megaphone className="whatsappBuilder-cardIcon" />
                                    </div>
                                }
                                title={
                                    <div className="whatsappBuilder-cardTitle">
                                        Template Category
                                        <IconButton
                                            size="small"
                                            className="whatsappBuilder-infoBtn"
                                        >
                                            <InfoIcon className="whatsappBuilder-infoIcon" />
                                        </IconButton>
                                    </div>
                                }
                                subheader={
                                    <div className="whatsappBuilder-cardSubtitle">
                                        Choose how this template will be classified
                                    </div>
                                }
                            />

                            <div className="whatsappBuilder-categorySelector">
                                <button
                                    type="button"
                                    className={`whatsappBuilder-categoryOption ${
                                        category === 'UTILITY' ? 'isActive' : ''
                                    }`}
                                    onClick={() =>
                                        handleCategoryChange('UTILITY')
                                    }
                                >
                                    <div className="whatsappBuilder-categoryOptionContent">
                                        <div className="whatsappBuilder-categoryLeadingIcon utility">
                                            <span>↗</span>
                                        </div>

                                        <div className="whatsappBuilder-categoryText">
                                            <span className="whatsappBuilder-categoryName">
                                                Utility
                                            </span>

                                            <span className="whatsappBuilder-categoryDescription">
                                                Updates &amp; notifications
                                            </span>
                                        </div>

                                        <div className="whatsappBuilder-categoryCheck">
                                            ✓
                                        </div>
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    className={`whatsappBuilder-categoryOption ${
                                        category === 'MARKETING' ? 'isActive' : ''
                                    }`}
                                    onClick={() =>
                                        handleCategoryChange('MARKETING')
                                    }
                                >
                                    <div className="whatsappBuilder-categoryOptionContent">
                                        <div className="whatsappBuilder-categoryLeadingIcon marketing">
                                            <Megaphone size={17} />
                                        </div>

                                        <div className="whatsappBuilder-categoryText">
                                            <span className="whatsappBuilder-categoryName">
                                                Marketing
                                            </span>

                                            <span className="whatsappBuilder-categoryDescription">
                                                Offers &amp; promotions
                                            </span>
                                        </div>

                                        <div className="whatsappBuilder-categoryCheck">
                                            ✓
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </div>
                        <div className="whatsappBuilder-card">
                            <CardHeader
                                className="whatsappBuilder-cardHeader"
                                avatar={<LocalOfferIcon className="whatsappBuilder-cardIcon" />}
                                title={
                                    <div className="whatsappBuilder-cardTitle">
                                        Template Name
                                        <IconButton size="small" className="whatsappBuilder-infoBtn">
                                            <InfoIcon className="whatsappBuilder-infoIcon" />
                                        </IconButton>
                                    </div>
                                }
                                subheader={
                                    <div className="whatsappBuilder-cardSubtitle">
                                        Lowercase letters, numbers &amp; underscores only
                                    </div>
                                }
                            />
                            <FormControl className="whatsappBuilder-formControl">
                                <TextField
                                    id="templateName"
                                    label="Template Name"
                                    variant="outlined"
                                    fullWidth
                                    value={templateName}
                                    onChange={handleTemplateNameChange}
                                    error={errors.templateName}
                                />
                            </FormControl>
                        </div>
                        <div className="whatsappBuilder-sectionLabel">
                            Content
                        </div>
                        <div className="whatsappBuilder-card">
                            <CardHeader
                                className="whatsappBuilder-cardHeader"
                                avatar={<ImageIcon className="whatsappBuilder-cardIcon" />}
                                title={
                                    <div className="whatsappBuilder-cardTitle">
                                        Header
                                        <IconButton size="small" className="whatsappBuilder-infoBtn">
                                            <InfoIcon className="whatsappBuilder-infoIcon" />
                                        </IconButton>
                                    </div>
                                }
                                subheader={
                                    <div className="whatsappBuilder-cardSubtitle">
                                        Optional — text, image, video or document
                                    </div>
                                }
                                action={
                                    <Switch checked={headerIncluded} onChange={handleHeaderToggle} color="default" />
                                }
                            />
                            <Collapse in={headerIncluded}>
                                <FormControl className="whatsappBuilder-collapseFormControl">
                                    <Select value={headerFormat ?? 'text'} onChange={handleHeaderFormatChange} fullWidth>
                                        <MenuItem value="text">Text</MenuItem>
                                        <MenuItem value="image">Image</MenuItem>
                                        <MenuItem value="video">Video</MenuItem>
                                        <MenuItem value="document">Document</MenuItem>
                                    </Select>
                                </FormControl>
                                {renderHeaderInput()}
                                {errors.header && (
                                    <div className="whatsappBuilder-fieldError">
                                        {errors.header}
                                    </div>
                                )}
                                {detectedHeaderVariables.length > 0 && (
                                    <div className="whatsappBuilder-varDefaults">
                                        <div className="whatsappBuilder-varDefaultsTitle">
                                            Variable Sample Values
                                        </div>
                                        {detectedHeaderVariables.map((v, index) => (
                                            <div key={index} className="whatsappBuilder-varRow">
                                                <div className="whatsappBuilder-varTag">{v}</div>
                                                <TextField
                                                    size="small"
                                                    placeholder="Enter default value"
                                                    value={headerVariableExamples[v] || ''}
                                                    onChange={e => setHeaderVariableExamples(prev => ({ ...prev, [v]: e.target.value }))}
                                                    fullWidth
                                                    error={!!errors[`variable_${v}`]}
                                                    helperText={errors[`variable_${v}`] || ''}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Collapse>
                        </div>
                        <div className="whatsappBuilder-card">
                            <CardHeader
                                className="whatsappBuilder-cardHeader"
                                avatar={<ChatBubbleOutlineIcon className="whatsappBuilder-cardIcon" />}
                                title={
                                    <div className="whatsappBuilder-cardTitleBlock">
                                        <div className="whatsappBuilder-cardTitle">Body</div>
                                        <div className="whatsappBuilder-cardSubtitle">
                                            Required · Max 1024 chars
                                        </div>
                                    </div>
                                }
                            />
                            <div className="whatsappBuilder-editorWrap">
                                <textarea
                                    ref={bodyRef}
                                    className="whatsappBuilder-textarea"
                                    value={body}
                                    onChange={e => handleBodyChange(e.target.value)}
                                    maxLength={1024}
                                />
                                <div className="whatsappBuilder-charCounter">
                                    {body.length}/1024
                                </div>
                                <div className="whatsappBuilder-toolbar">
                                    <div className="whatsappBuilder-toolbarLeft">
                                        <IconButton size="small" className="whatsappBuilder-fmtBtn" onClick={() => applyFormat('*')}>
                                            <b>B</b>
                                        </IconButton>
                                        <IconButton size="small" className="whatsappBuilder-fmtBtn" onClick={() => applyFormat('_')}>
                                            <i>I</i>
                                        </IconButton>
                                        <IconButton size="small" className="whatsappBuilder-fmtBtn whatsappBuilder-fmtBtnStrike" onClick={() => applyFormat('~')}>
                                            S
                                        </IconButton>
                                        <IconButton size="small" className="whatsappBuilder-fmtBtn whatsappBuilder-fmtBtnStrike" onClick={() => setShowEmoji(!showEmoji)}>
                                            😊
                                        </IconButton>
                                    </div>
                                    <div className="whatsappBuilder-toolbarRight">
                                        <Button size="small" className="whatsappBuilder-addVarBtn" onClick={handleAddVariable}>
                                            + Add variable
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            {showEmoji && (
                                <div className="emojiPickerWrapper">
                                    <EmojiPicker onEmojiClick={handleEmojiClick} searchDisabled />
                                </div>
                            )}
                            {detectedVariables.length > 0 && (
                                <div className="whatsappBuilder-varDefaults">
                                    <div className="whatsappBuilder-varDefaultsTitle">
                                        Variable Sample Values
                                    </div>
                                    {detectedVariables.map((v, index) => (
                                        <div key={index} className="whatsappBuilder-varRow">
                                            <div className="whatsappBuilder-varTag">{v}</div>
                                            <TextField
                                                size="small"
                                                placeholder="Enter default value"
                                                value={variableExamples[v] || ''}
                                                onChange={e => setVariableExamples(prev => ({ ...prev, [v]: e.target.value }))}
                                                error={!!errors[`variable_${v}`]}
                                                helperText={errors[`variable_${v}`] || ''}
                                                fullWidth
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="whatsappBuilder-card">
                            <CardHeader
                                className="whatsappBuilder-cardHeader"
                                avatar={<FlagIcon className="whatsappBuilder-cardIcon" />}
                                title={
                                    <div className="whatsappBuilder-cardTitle">
                                        Footer text
                                        <IconButton size="small" className="whatsappBuilder-infoBtn">
                                            <InfoIcon className="whatsappBuilder-infoIcon" />
                                        </IconButton>
                                    </div>
                                }
                                subheader={
                                    <div className="whatsappBuilder-cardSubtitle">
                                        Optional · Max 60 chars
                                    </div>
                                }
                                action={
                                    <Switch checked={footerIncluded} onChange={handleFooterToggle} color="default" />
                                }
                            />
                            <Collapse in={footerIncluded}>
                                <FormControl className="whatsappBuilder-footerInputBox">
                                    <Input
                                        id="footer"
                                        disableUnderline
                                        multiline
                                        fullWidth
                                        rows={1}
                                        value={footer}
                                        onChange={e => setFooter(e.target.value)}
                                        inputProps={{ maxLength: 60 }}
                                        sx={{background: 'transparent', p: 1}}
                                    />
                                    <Input
                                        fullWidth
                                        disabled
                                        disableUnderline
                                        sx={{pr: 1, position: 'absolute', bottom: 0, right: 0, width: 'max-content'}}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <div className="whatsappBuilder-footerCount">
                                                    {footer.length}/60
                                                </div>
                                            </InputAdornment>
                                        }
                                    />
                                </FormControl>
                            </Collapse>
                        </div>
                        <div className="whatsappBuilder-card">
                            <CardHeader
                                className="whatsappBuilder-cardHeader"
                                avatar={<Crop169Icon className="whatsappBuilder-cardIcon" />}
                                title={
                                    <div className="whatsappBuilder-cardTitle">
                                        Buttons
                                        <IconButton size="small" className="whatsappBuilder-infoBtn">
                                            <InfoIcon className="whatsappBuilder-infoIcon" />
                                        </IconButton>
                                    </div>
                                }
                                subheader={
                                    <div className="whatsappBuilder-cardSubtitle">
                                        Optional · Up to 3 buttons
                                    </div>
                                }
                                action={
                                    <Switch checked={buttonsIncluded} onChange={handleButtonsToggle} color="default" />
                                }
                            />
                            <Collapse in={buttonsIncluded}>
                                {buttons.map((button, index) => {
                                    const typeIcons = {
                                        QUICK_REPLY: <MessageCircle size={15} />,
                                        URL: <Link size={15} />,
                                        PHONE_NUMBER: <Phone size={15} />,
                                        COPY_CODE: <Copy size={15} />,
                                    };
                                    return (
                                        <div key={index} className="whatsappBuilder-buttonItem" data-type={button.type}>
                                            <div className="whatsappBuilder-buttonItemHeader">
                                                <div className="whatsappBuilder-buttonItemLeft">
                                                    <div className="whatsappBuilder-buttonTypeIcon">
                                                        {typeIcons[button.type]}
                                                    </div>
                                                    <div className="whatsappBuilder-buttonLabel">
                                                        Button {index + 1}
                                                    </div>
                                                </div>
                                                <IconButton
                                                    size="small"
                                                    className="whatsappBuilder-deleteBtn"
                                                    onClick={() => handleDeleteButton(index)}
                                                >
                                                    <Trash2 size={15} />
                                                </IconButton>
                                            </div>
                                            <div className="whatsappBuilder-buttonFields">
                                                <TextField
                                                    select size="small" fullWidth label="Button Type"
                                                    value={button.type}
                                                    onChange={e => handleButtonChange(index, 'type', e.target.value)}
                                                >
                                                    <MenuItem value="QUICK_REPLY">Quick Reply</MenuItem>
                                                    <MenuItem value="URL">URL</MenuItem>
                                                    <MenuItem value="PHONE_NUMBER">Phone Number</MenuItem>
                                                    <MenuItem value="COPY_CODE">Copy Code</MenuItem>
                                                </TextField>
                                                <TextField
                                                    size="small" fullWidth label="Button Text"
                                                    value={button.text}
                                                    onChange={e => handleButtonChange(index, 'text', e.target.value)}
                                                    inputProps={{ maxLength: 25 }}
                                                    helperText={`${button.text.length}/25 characters`}
                                                />
                                                {button.type == 'PHONE_NUMBER' ?
                                                    <PhoneInput
                                                        international
                                                        defaultCountry="IN"
                                                        value={contactNumber}
                                                        onChange={e => handleContactNumberChange(index, e)}
                                                        className={`whatsappBuilder__phoneField`}
                                                    />
                                                : button.type !== 'QUICK_REPLY' && (
                                                    <TextField
                                                        size="small" fullWidth
                                                        label={
                                                            button.type === 'URL' ? 'Website URL'
                                                                : button.type === 'PHONE_NUMBER' ? 'Phone Number'
                                                                    : 'Coupon Code'
                                                        }
                                                        placeholder={
                                                            button.type === 'URL' ? 'https://example.com'
                                                                : button.type === 'PHONE_NUMBER' ? '+1 234 567 8901'
                                                                    : 'SAVE20'
                                                        }
                                                        value={button.value}
                                                        onChange={e => handleButtonChange(index, 'value', e.target.value)}
                                                    />
                                                )}
                                                {contactNumber && (!isPossibleNumber || !isValidNumber) && (
                                                    <Typography variant="caption" color="error">
                                                        Invalid phone number
                                                    </Typography>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                {buttons.length < 3 && (
                                    <div className="whatsappBuilder-addButtonWrap">
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            className="whatsappBuilder-addButtonBtn"
                                            onClick={handleAddButton}
                                            disabled={buttons.length > 0 && !buttons[buttons.length - 1]?.text}
                                        >
                                            + Add Button
                                        </Button>
                                    </div>
                                )}
                            </Collapse>
                        </div>
                        <div className="whatsappBuilder-saveWrap">
                            <Button
                                variant="contained"
                                size="large"
                                fullWidth
                                className="whatsappBuilder-saveBtn"
                                disabled={cursor === 'wait'}
                                onClick={handleSaveTemplate}>
                                Save Template
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="whatsappBuilder-right">
                    <div className="whatsappBuilder-previewSticky">
                        <div className="whatsappBuilder-phoneShell">
                            <div className="whatsappBuilder-phoneHeader">
                                <div className="whatsappBuilder-phoneAvatar flex-row-center">
                                    <WhatsAppIcon className="whatsappBuilder-phoneAvatarIcon" />
                                </div>
                                <div className="whatsappBuilder-phoneContact">
                                    <div className="whatsappBuilder-phoneContactName">
                                        Marketing Bot
                                    </div>
                                    <div className="whatsappBuilder-phoneContactStatus">
                                        template preview
                                    </div>
                                </div>
                                <span className='whatsappBuilder-brandTitle__category'>
                                    {category}
                                </span>
                            </div>
                            <div className="whatsappBuilder-chatArea">
                                <div className="whatsappBuilder-bubble">
                                    {headerIncluded && header.format === 'text' && header.str && (
                                        <div className="whatsappBuilder-bubbleHeaderText">
                                            {header.str.replace('{{1}}', headerVariableExamples['{{1}}']?.trim() || '{{1}}')}
                                        </div>
                                    )}
                                    {headerIncluded && header.format === 'image' && header.str && (
                                        <img src={header.str} className="whatsappBuilder-bubbleMedia" alt="header" />
                                    )}

                                    {headerIncluded && header.format === 'video' && header.str && (
                                        <video controls width="220" style={{borderRadius:8}}>
                                            <source src={header.str} type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                    )}
                                    <div
                                        className="whatsappBuilder-bubbleBody"
                                        dangerouslySetInnerHTML={{ __html: renderWhatsAppFormatting(body) }}
                                    />
                                    {footerIncluded && footer && (
                                        <div className="whatsappBuilder-bubbleFooter">
                                            {footer}
                                        </div>
                                    )}
                                    <div className="whatsappBuilder-bubbleTimestamp">
                                        12:34 PM ✓✓
                                    </div>
                                    {buttonsIncluded && buttons.some(b => b.text) && (
                                        <div className="whatsappBuilder-bubbleButtons">
                                            {buttons.map((btn, i) =>
                                                btn.text ? (
                                                    <div key={i} className="whatsappBuilder-bubbleButton">
                                                        {btn.text}
                                                    </div>
                                                ) : null
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="whatsappBuilder-previewLabel">
                                Live Preview
                            </div>
                        </div>
                        {allErrorMessages.length > 0 && (
                            <div className="whatsappBuilder-errorBox">
                                <div className="whatsappBuilder-errorBoxTitle">
                                    ⚠️ Please fix the following errors:
                                </div>
                                {allErrorMessagesWithField.map((msg, i) => (
                                    <div key={i} className="whatsappBuilder-errorBoxItem">
                                        • {msg}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default WhatsappTemplateBuilder;